// Aba "🔎 Conciliação" de Remoções: cada remoção cobrada pelo fornecedor (prévia da SOS por e-mail)
// com os sinais Pedido (IW) · No Quality · Aditivo (IW) · Captação, e as pendências para o
// financeiro. Montada no servidor (api/_conciliacao-remocoes.js).
import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { supabase } from '../supabase';
import { ArquivoLink } from '../arquivos';
import { nomeUnidade } from './AphSOS';

const P = { bg: '#f8fafc', white: '#fff', border: '#e2e8f0', text: '#1e293b', muted: '#64748b', primary: '#6366f1' };
const CATS = [
  { k: 'Eletiva', l: '🗓 Eletivas', desc: 'fora do pacote: aditivo no IW' },
  { k: 'Transferência', l: '🔁 Transferências (TIH)', desc: 'entre hospitais: aditivo no IW' },
  { k: 'APH', l: '🚨 APH (urgência)', desc: 'pré-hospitalar' },
  { k: 'Desospitalização', l: '🏥 Desospitalização / alta', desc: 'ligadas à Captação' },
  { k: 'Outra', l: '❔ Outras', desc: 'conferir o motivo' },
  { k: '', l: 'Todas', desc: '' },
];
const fmtData = iso => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : '—');
const fmtValor = v => (typeof v === 'number' ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—');
const chip = (fundo, cor) => ({ fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: fundo, color: cor, whiteSpace: 'nowrap', display: 'inline-block' });
const CONF = { certo: ['#dcfce7', '#166534', 'IW ok'], provavel: ['#fef9c3', '#854d0e', 'provável'], verificar: ['#fee2e2', '#991b1b', 'verificar'], 'nao encontrado': ['#f1f5f9', '#475569', 'não achado no IW'] };
const PEND_COR = { 'falta aditivo no IW': ['#fee2e2', '#991b1b'], 'sem aditivo (custo no pacote)': ['#ffedd5', '#9a3412'], 'verificar paciente': ['#fef3c7', '#92400e'] };

function Sinal({ ok, texto, titulo }) {
  return <div title={titulo} style={{ fontSize: '0.74rem', color: ok ? '#166534' : '#94a3b8', whiteSpace: 'nowrap' }}>{ok ? '●' : '○'} {texto}</div>;
}

export default function ConciliacaoRemocoes() {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [cat, setCat] = useState('Eletiva');
  const [pend, setPend] = useState('');
  const [mes, setMes] = useState('');
  const [busca, setBusca] = useState('');

  const carregar = async () => {
    setCarregando(true); setErro('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/conciliacao-remocoes', { headers: { Authorization: `Bearer ${session?.access_token || ''}` } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `Erro ${r.status}`);
      setDados(j);
    } catch (e) { setErro(e.message); }
    setCarregando(false);
  };
  useEffect(() => { carregar(); }, []);

  const linhas = dados?.linhas || [];
  const meses = useMemo(() => [...new Set(linhas.map(l => l.data.slice(0, 7)))].sort().reverse(), [linhas]);
  const daCat = useMemo(() => linhas.filter(l => (!cat || l.categoria === cat) && (!mes || l.data.startsWith(mes))), [linhas, cat, mes]);
  const pendencias = useMemo(() => {
    const c = {};
    daCat.forEach(l => l.pendencias.forEach(p => { c[p] = (c[p] || 0) + 1; }));
    return Object.entries(c).sort((a, b) => b[1] - a[1]);
  }, [daCat]);
  const q = busca.trim().toLowerCase();
  const vis = useMemo(() => daCat.filter(l => (!pend || (pend === 'ok' ? !l.pendencias.length : l.pendencias.includes(pend)))
    && (!q || `${l.paciente} ${l.identificacao.nomeIW} ${l.motivo} ${l.destino}`.toLowerCase().includes(q))), [daCat, pend, q]);
  const soma = arr => arr.reduce((s, x) => s + (x.valor || 0), 0);

  if (carregando) return <div style={{ color: P.muted }}>Cruzando prévias da SOS, IW e Quality…</div>;
  if (erro) return <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 10, padding: '10px 14px' }}>Não foi possível carregar: {erro}</div>;
  const r = dados.resumo;
  const th = { padding: '8px 10px', textAlign: 'left', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '.04em', color: P.muted, background: P.bg, whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', verticalAlign: 'top', borderTop: '1px solid #f1f5f9', fontSize: '0.8rem' };
  const cartao = (rotulo, valor, sub, cor) => (
    <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 12, padding: '10px 14px', minWidth: 170 }}>
      <div style={{ fontSize: '0.72rem', color: P.muted, fontWeight: 600 }}>{rotulo}</div>
      <div style={{ fontSize: '1.35rem', fontWeight: 800, color: cor }}>{valor}</div>
      {sub && <div style={{ fontSize: '0.72rem', color: P.muted }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: '0.8rem', color: P.muted }}>
        Remoções <strong>cobradas pela SOS Vida</strong> (planilhas das prévias recebidas no e-mail do Núcleo) cruzadas com o IW (pedido, aditivo, cadastro do paciente), o Quality (remoções e Captação) e as Regras de Contrato.
        <strong>Toda remoção precisa de aditivo no IW</strong>: eletiva e transferência (fora do pacote) para cobrar; as demais para registrar o custo dentro do pacote. Montado em {new Date(dados.atualizadoEm).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}.
        <button onClick={carregar} style={{ marginLeft: 8, border: `1px solid ${P.border}`, background: P.white, borderRadius: 8, padding: '3px 8px', cursor: 'pointer', display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: '0.74rem' }}><RefreshCw size={12} /> Atualizar</button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {cartao('Cobradas pela SOS', r.cobradas, fmtValor(r.valorCobrado), P.text)}
        {cartao('🔴 Falta aditivo (fora do pacote)', r.faltaAditivo.qtd, fmtValor(r.faltaAditivo.valor), '#991b1b')}
        {cartao('🟠 Sem aditivo (custo no pacote)', r.semAditivoPacote?.qtd ?? 0, fmtValor(r.semAditivoPacote?.valor ?? 0), '#9a3412')}
        {cartao('Cobradas e fora do Quality', r.foraDoQuality, 'incluir no sistema', '#9a3412')}
        {cartao('Paciente para verificar', r.verificar, 'nome / atendimento não fecham', '#92400e')}
        {cartao('Remoções p/ troca de cânula', r.trocasCanula, 'a troca aconteceu (hospital)', '#1e40af')}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {CATS.map(c => {
          const lista = linhas.filter(l => (!c.k || l.categoria === c.k) && (!mes || l.data.startsWith(mes)));
          const ativo = cat === c.k;
          return (
            <button key={c.l} onClick={() => { setCat(c.k); setPend(''); }} title={c.desc}
              style={{ border: `1px solid ${ativo ? P.primary : P.border}`, background: ativo ? '#eef2ff' : P.white, color: ativo ? P.primary : P.muted, borderRadius: 999, padding: '6px 14px', fontSize: '0.8rem', fontWeight: ativo ? 800 : 600, cursor: 'pointer' }}>
              {c.l} · {lista.length} · {fmtValor(soma(lista))}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: '0.76rem', color: P.muted }}>Pendência:</span>
        {[['', 'Todas'], ...pendencias.map(([p, n]) => [p, `${p} · ${n}`]), ['ok', `Sem pendência · ${daCat.filter(l => !l.pendencias.length).length}`]].map(([k, l]) => {
          const ativo = pend === k;
          const [fundo, cor] = PEND_COR[k] || ['#f1f5f9', '#334155'];
          return <button key={k || 'todas'} onClick={() => setPend(k)} style={{ ...chip(ativo ? fundo : P.white, cor), border: `1px solid ${ativo ? cor : P.border}`, cursor: 'pointer', padding: '4px 10px' }}>{l}</button>;
        })}
        <select value={mes} onChange={e => setMes(e.target.value)} style={{ marginLeft: 'auto', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.8rem' }}>
          <option value="">Todos os meses</option>
          {meses.map(m => <option key={m} value={m}>{m.slice(5)}/{m.slice(0, 4)}</option>)}
        </select>
        <label style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: -9999 }}>Buscar paciente ou motivo</span>
          <Search size={14} style={{ position: 'absolute', left: 9, top: 9, color: '#94a3b8' }} />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Paciente ou motivo" style={{ padding: '6px 8px 6px 28px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.8rem', width: 190 }} />
        </label>
      </div>

      <div style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1200 }}>
            <thead><tr>{['Data', 'Paciente', 'Motivo · trajeto', 'Valor', 'Pedido no IW', 'No Quality', 'Aditivo', 'Pendências', 'Prévia'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {vis.length === 0 && <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: P.muted, padding: 26 }}>Nada neste filtro.</td></tr>}
              {vis.map(l => {
                const [cf, cc, ct] = CONF[l.identificacao.confianca] || CONF['nao encontrado'];
                return (
                  <tr key={l.id} style={{ background: l.pendencias.includes('falta aditivo no IW') ? '#fff7f7' : undefined }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtData(l.data)}<div style={{ fontSize: '0.7rem', color: P.muted }}>{nomeUnidade(l.unidade)}</div></td>
                    <td style={{ ...td, fontWeight: 700, maxWidth: 230 }}>
                      {l.paciente}
                      <div style={{ marginTop: 3, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <span style={chip(cf, cc)} title={l.identificacao.motivo || ''}>{ct}</span>
                        {l.identificacao.numIW && <span style={{ fontSize: '0.7rem', color: P.muted, fontWeight: 500 }}>IW {l.identificacao.numIW}{l.identificacao.atendimentos > 1 ? ` · ${l.identificacao.atendimentos} atendimentos` : ''}</span>}
                      </div>
                      {l.identificacao.nomeIW && l.identificacao.confianca !== 'certo' && <div style={{ fontSize: '0.7rem', color: '#92400e', fontWeight: 500 }}>no IW: {l.identificacao.nomeIW}{l.identificacao.motivo ? ` — ${l.identificacao.motivo}` : ''}</div>}
                      <div style={{ fontSize: '0.7rem', color: P.muted, fontWeight: 500 }}>{l.convenio}</div>
                    </td>
                    <td style={{ ...td, maxWidth: 260 }}>
                      <span style={chip('#eef2ff', '#3730a3')}>{l.categoria}</span>{l.trocaCanula && <span style={{ ...chip('#dbeafe', '#1e40af'), marginLeft: 4 }}>troca de cânula</span>}
                      <div style={{ marginTop: 3 }}>{l.motivo || '—'}</div>
                      <div style={{ fontSize: '0.72rem', color: P.muted }}>{l.origem} → {l.destino}</div>
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700 }}>{fmtValor(l.valor)}</td>
                    <td style={td}><Sinal ok={!!l.pedidoIW} texto={l.pedidoIW ? fmtData(l.pedidoIW.data) : 'não'} titulo={l.pedidoIW ? `${l.pedidoIW.profissional}: ${l.pedidoIW.pedido}` : ''} /></td>
                    <td style={td}>
                      <Sinal ok={!!l.quality} texto={l.quality ? `${l.quality.aba}${l.quality.status ? ` · ${l.quality.status}` : ''}` : 'não'} />
                      {l.categoria === 'Desospitalização' && <Sinal ok={!!l.captacao} texto={l.captacao ? `Captação · ${l.captacao.status}` : 'Captação: não'} />}
                    </td>
                    <td style={td}>
                      <Sinal ok={!!l.aditivo} texto={l.aditivo ? (l.aditivo.origem === 'IW' ? `IW ${fmtData(l.aditivo.data)} · interna ${l.aditivo.interna}/3 · operadora ${l.aditivo.externa}` : l.aditivo.origem) : 'FALTA'} />
                      <div style={{ fontSize: '0.7rem', color: P.muted }} title={l.regra ? `${l.regra.contrato} — incluso: ${l.regra.incluso || '—'} · excluso: ${l.regra.excluso || '—'}` : ''}>{l.foraDoPacote ? 'fora do pacote' : 'custo no pacote'}{l.regra ? ' · ver regra' : ''}</div>
                    </td>
                    <td style={{ ...td, maxWidth: 220 }}>
                      {l.pendencias.length === 0 ? <span style={chip('#dcfce7', '#166534')}>ok</span>
                        : l.pendencias.map(p => { const [f, c] = PEND_COR[p] || ['#ffedd5', '#9a3412']; return <div key={p} style={{ marginBottom: 2 }}><span style={chip(f, c)}>{p}</span></div>; })}
                    </td>
                    <td style={td}>
                      {l.cobranca?.arquivo?.url
                        ? <ArquivoLink href={l.cobranca.arquivo.url} target="_blank" rel="noreferrer" title={l.cobranca.assunto} style={{ fontSize: '0.72rem', color: '#1e40af' }}>📎 planilha</ArquivoLink>
                        : '—'}
                      <div style={{ fontSize: '0.68rem', color: P.muted }}>{fmtData(l.cobranca?.recebidoEm)}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '8px 12px', borderTop: `1px solid ${P.border}`, fontSize: '0.78rem', color: P.muted }}>
          {vis.length} remoção(ões) · {fmtValor(soma(vis))} · "verificar" = o nome ou o atendimento do paciente não fecham entre SOS e IW (o nº de atendimento muda a cada nova admissão).
        </div>
      </div>
    </div>
  );
}
